/**
 * Single source of truth for Abby's one-shot first-session welcome.
 * Imported by:
 *   - backend/sideband/greeting-branch.ts (live realtime delivery)
 *   - apps/mobile/components/abby/AbbyWelcomeCard.tsx (v1 visual fallback)
 *   - apps/mobile/components/abby/AbbyWelcomeCardV2.tsx (v2 pre-recorded audio + bubble stream)
 *   - scripts/generate-welcome-audio.ts (TTS asset generator)
 *
 * Peninsular Spanish — informal tú/vosotros, no usted, no LatAm vocabulary.
 *
 * Editing rules:
 *   - The 9-paragraph count is load-bearing: timing JSON sidecars and
 *     CloudWatch dashboards assume exactly 9.
 *   - Edit copy here, then run `npm run gen:welcome-audio` to regenerate
 *     the mp3 + json assets.
 *   - The asset generator hashes the script text; a no-op edit is detected
 *     and skipped to keep CI cheap.
 */

export const WELCOME_SCRIPT_EN = `Hi, I'm Abby — your private AI matchmaker.

I'm not here to make you swipe everyday, perform, or compete for attention. I'm here to understand you deeply, so I can help find the person who truly fits you.

Everything you share is 100% confidential. Your answers are not shown publicly, and your profile is never revealed casually. Abby is designed to protect your privacy while helping you build a real connection.

Our matching process is backed by expert psychology, so the questions I ask are not random. They help uncover your values, personality, attraction patterns, relationship goals, lifestyle, communication style, and the deeper traits that shape long-term compatibility.

And when you need support, I can also help with dating advice — from understanding a match, to preparing for a date, to navigating communication with more confidence.

So answer honestly — not perfectly. The more real you are, the better I can understand what you need, build your compatibility report, and introduce you to someone genuinely worth meeting.

I will be here every day to help you navigate the App, fill-out your answers and anything else you may need.

At Abby, we care about finding your ideal match — not keeping you endlessly searching.

Let's begin.`;

export const WELCOME_SCRIPT_ES = `Hola, soy Abby — tu matchmaker privada con IA.

No estoy aquí para que pases el día deslizando, actuando o compitiendo por atención. Estoy aquí para entenderte de verdad, para que pueda ayudarte a encontrar a la persona que de verdad encaja contigo.

Todo lo que compartes es 100% confidencial. Tus respuestas no se muestran públicamente, y tu perfil nunca se revela a la ligera. Abby está diseñada para proteger tu privacidad mientras te ayuda a construir una conexión real.

Nuestro proceso de matching está respaldado por psicología experta, así que las preguntas que te hago no son aleatorias. Ayudan a descubrir tus valores, personalidad, patrones de atracción, objetivos de pareja, estilo de vida, forma de comunicarte y los rasgos más profundos que dan forma a la compatibilidad a largo plazo.

Y cuando necesites apoyo, también puedo ayudarte con consejos — desde entender a un match, prepararte para una cita, hasta gestionar la comunicación con más confianza.

Así que responde con honestidad — no con perfección. Cuanto más auténtico/a seas, mejor podré entender lo que necesitas, construir tu informe de compatibilidad y presentarte a alguien que de verdad merezca la pena conocer.

Estaré aquí cada día para ayudarte a moverte por la app, contestar tus preguntas y cualquier otra cosa que necesites.

En Abby nos importa encontrar tu match ideal — no mantenerte buscando sin parar.

Empecemos.`;

export type WelcomeLocale = 'en' | 'es';

export function getWelcomeScript(locale: WelcomeLocale): string {
  return locale === 'es' ? WELCOME_SCRIPT_ES : WELCOME_SCRIPT_EN;
}
